import { useState, useEffect, useCallback } from 'react'
import { getNexusClient } from '@/lib/supabase/client'
import { useAuth } from '@/lib/auth/context'
import { generateRoomId, generateRoomLink } from '@/lib/livekit/tokenService'
import { mailApi } from '@/lib/supabase/mail'
import {
  CalendarEvent,
  CalendarEventRow,
  EventAttendee,
  CalendarEventAttendeeRow,
  CreateEventInput,
  UpdateEventInput,
  UseCalendarEventsOptions,
  eventRowToEvent,
  attendeeRowToAttendee,
} from '@/types/calendar'

// ============================================================================
// useCalendarEvents - Fetch events for a date range
// ============================================================================

export function useCalendarEvents(options: UseCalendarEventsOptions) {
  const { startDate, endDate, ownerId, includeAttendees = true } = options
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchEvents = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      let eventRows: CalendarEventRow[] = []

      if (ownerId) {
        // Fetch events where user is OWNER
        const { data: ownedEvents, error: ownedError } = await (client as any)
          .from('calendar_events')
          .select('*')
          .eq('owner_id', ownerId)
          .gte('start_time', startDate)
          .lte('end_time', endDate) as { data: CalendarEventRow[] | null; error: any }

        if (ownedError) throw ownedError

        // Fetch event IDs where user is an ATTENDEE
        const { data: attendeeRecords } = await (client as any)
          .from('calendar_event_attendees')
          .select('event_id')
          .eq('admin_user_id', ownerId) as { data: { event_id: string }[] | null }

        // Fetch those events (where user is invited but not owner)
        let invitedEvents: CalendarEventRow[] = []
        if (attendeeRecords && attendeeRecords.length > 0) {
          const eventIds = attendeeRecords.map(r => r.event_id)
          const { data: invited } = await (client as any)
            .from('calendar_events')
            .select('*')
            .in('id', eventIds)
            .neq('owner_id', ownerId) // Exclude events user already owns
            .gte('start_time', startDate)
            .lte('end_time', endDate) as { data: CalendarEventRow[] | null }

          invitedEvents = invited || []
        }

        // Merge owned + invited events
        eventRows = [...(ownedEvents || []), ...invitedEvents]
      } else {
        // No owner filter - fetch all events in range
        const { data, error: eventError } = await (client as any)
          .from('calendar_events')
          .select('*')
          .gte('start_time', startDate)
          .lte('end_time', endDate) as { data: CalendarEventRow[] | null; error: any }

        if (eventError) throw eventError
        eventRows = data || []
      }

      // Sort by start time
      eventRows.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())

      if (eventRows.length === 0) {
        setEvents([])
        return
      }

      // Fetch attendees if requested
      let attendeesByEvent: Record<string, EventAttendee[]> = {}

      if (includeAttendees) {
        const eventIds = eventRows.map((e: CalendarEventRow) => e.id)
        const { data: attendeeRows } = await (client as any)
          .from('calendar_event_attendees')
          .select(`
            *,
            admin_users:admin_user_id (
              id,
              email,
              first_name,
              last_name,
              avatar_url
            )
          `)
          .in('event_id', eventIds) as { data: (CalendarEventAttendeeRow & { admin_users?: any })[] | null }

        if (attendeeRows) {
          attendeesByEvent = attendeeRows.reduce((acc, row) => {
            const attendee = attendeeRowToAttendee(row)
            if (row.admin_users) {
              attendee.user = {
                id: row.admin_users.id,
                email: row.admin_users.email,
                firstName: row.admin_users.first_name,
                lastName: row.admin_users.last_name,
                avatarUrl: row.admin_users.avatar_url,
              }
            }
            if (!acc[row.event_id]) {
              acc[row.event_id] = []
            }
            acc[row.event_id].push(attendee)
            return acc
          }, {} as Record<string, EventAttendee[]>)
        }
      }

      // Convert to CalendarEvent objects
      const events = eventRows.map((row: CalendarEventRow) =>
        eventRowToEvent(row, attendeesByEvent[row.id] || [])
      )

      setEvents(events)
    } catch (err: any) {
      console.error('Error fetching calendar events:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [startDate, endDate, ownerId, includeAttendees])

  useEffect(() => {
    fetchEvents()
  }, [fetchEvents])

  return {
    events,
    isLoading,
    error,
    refetch: fetchEvents,
  }
}

// ============================================================================
// useCalendarEvent - Fetch a single event by ID
// ============================================================================

export function useCalendarEvent(eventId: string | null) {
  const [event, setEvent] = useState<CalendarEvent | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!eventId) {
      setEvent(null)
      return
    }

    async function fetchEvent() {
      setIsLoading(true)
      setError(null)

      try {
        const client = getNexusClient()

        // Fetch event
        const { data: eventRow, error: eventError } = await (client as any)
          .from('calendar_events')
          .select('*')
          .eq('id', eventId)
          .single() as { data: CalendarEventRow | null; error: any }

        if (eventError) throw eventError
        if (!eventRow) {
          setEvent(null)
          return
        }

        // Fetch attendees
        const { data: attendeeRows } = await (client as any)
          .from('calendar_event_attendees')
          .select(`
            *,
            admin_users:admin_user_id (
              id,
              email,
              first_name,
              last_name,
              avatar_url
            )
          `)
          .eq('event_id', eventId) as { data: (CalendarEventAttendeeRow & { admin_users?: any })[] | null }

        const attendees = (attendeeRows || []).map(row => {
          const attendee = attendeeRowToAttendee(row)
          if (row.admin_users) {
            attendee.user = {
              id: row.admin_users.id,
              email: row.admin_users.email,
              firstName: row.admin_users.first_name,
              lastName: row.admin_users.last_name,
              avatarUrl: row.admin_users.avatar_url,
            }
          }
          return attendee
        })

        setEvent(eventRowToEvent(eventRow, attendees))
      } catch (err: any) {
        console.error('Error fetching calendar event:', err)
        setError(err.message)
      } finally {
        setIsLoading(false)
      }
    }

    fetchEvent()
  }, [eventId])

  return { event, isLoading, error }
}

// ============================================================================
// useCalendarMutations - Create, update, delete events
// ============================================================================

export function useCalendarMutations() {
  const { adminUser } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Create a new calendar event
  const createEvent = useCallback(async (input: CreateEventInput): Promise<CalendarEvent | null> => {
    if (!adminUser?.id) {
      setError('You must be logged in to create events')
      return null
    }

    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      // Generate meeting room if video conference enabled
      let meetingRoomId: string | undefined
      let meetingRoomLink: string | undefined

      if (input.hasVideoConference) {
        meetingRoomId = generateRoomId()
        meetingRoomLink = generateRoomLink(meetingRoomId)
      }

      // Insert event
      const eventData: Partial<CalendarEventRow> = {
        owner_id: adminUser.id,
        title: input.title,
        description: input.description || null,
        location: input.location || null,
        start_time: input.startTime,
        end_time: input.endTime,
        all_day: input.allDay || false,
        timezone: input.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        has_video_conference: input.hasVideoConference || false,
        meeting_room_id: meetingRoomId || null,
        meeting_room_link: meetingRoomLink || null,
        event_type: input.eventType || 'meeting',
        color: input.color || 'primary',
        is_private: input.isPrivate || false,
      }

      const { data: eventRow, error: eventError } = await (client as any)
        .from('calendar_events')
        .insert(eventData)
        .select()
        .single() as { data: CalendarEventRow | null; error: any }

      if (eventError) throw eventError
      if (!eventRow) throw new Error('Failed to create event')

      // Add organizer as first attendee
      const attendees: EventAttendee[] = []

      const { data: organizerRow } = await (client as any)
        .from('calendar_event_attendees')
        .insert({
          event_id: eventRow.id,
          admin_user_id: adminUser.id,
          response_status: 'accepted',
          is_organizer: true,
          is_optional: false,
        })
        .select()
        .single() as { data: CalendarEventAttendeeRow | null }

      if (organizerRow) {
        attendees.push({
          ...attendeeRowToAttendee(organizerRow),
          user: {
            id: adminUser.id,
            email: adminUser.email,
            firstName: adminUser.firstName || undefined,
            lastName: adminUser.lastName || undefined,
            avatarUrl: adminUser.avatarUrl || undefined,
          },
        })
      }

      // Add other attendees
      if (input.attendees && input.attendees.length > 0) {
        for (const attendeeInput of input.attendees) {
          const attendeeData: Partial<CalendarEventAttendeeRow> = {
            event_id: eventRow.id,
            admin_user_id: attendeeInput.adminUserId || null,
            external_email: attendeeInput.externalEmail || null,
            external_name: attendeeInput.externalName || null,
            response_status: 'pending',
            is_organizer: false,
            is_optional: attendeeInput.isOptional || false,
          }

          const { data: attendeeRow } = await (client as any)
            .from('calendar_event_attendees')
            .insert(attendeeData)
            .select()
            .single() as { data: CalendarEventAttendeeRow | null }

          if (attendeeRow) {
            attendees.push(attendeeRowToAttendee(attendeeRow))
          }
        }
      }

      // Send email invites if requested
      if (input.sendInvites && input.attendees && input.attendees.length > 0) {
        await sendMeetingInvites(eventRow, input.attendees, {
          id: adminUser.id,
          email: adminUser.email,
          firstName: adminUser.firstName || undefined,
          lastName: adminUser.lastName || undefined,
        }, input.sendFromAccountId)
      }

      return eventRowToEvent(eventRow, attendees)
    } catch (err: any) {
      console.error('Error creating calendar event:', err)
      setError(err.message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [adminUser])

  // Update an existing event
  const updateEvent = useCallback(async (input: UpdateEventInput): Promise<CalendarEvent | null> => {
    if (!adminUser?.id) {
      setError('You must be logged in to update events')
      return null
    }

    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      // Build update data
      const updateData: Partial<CalendarEventRow> = {}

      if (input.title !== undefined) updateData.title = input.title
      if (input.description !== undefined) updateData.description = input.description || null
      if (input.location !== undefined) updateData.location = input.location || null
      if (input.startTime !== undefined) updateData.start_time = input.startTime
      if (input.endTime !== undefined) updateData.end_time = input.endTime
      if (input.allDay !== undefined) updateData.all_day = input.allDay
      if (input.timezone !== undefined) updateData.timezone = input.timezone
      if (input.eventType !== undefined) updateData.event_type = input.eventType
      if (input.color !== undefined) updateData.color = input.color
      if (input.isPrivate !== undefined) updateData.is_private = input.isPrivate

      // Handle video conference toggle
      if (input.hasVideoConference !== undefined) {
        updateData.has_video_conference = input.hasVideoConference
        if (input.hasVideoConference) {
          // Generate new room if enabling
          const { data: existingEvent } = await (client as any)
            .from('calendar_events')
            .select('meeting_room_id')
            .eq('id', input.id)
            .single()

          if (!existingEvent?.meeting_room_id) {
            const roomId = generateRoomId()
            updateData.meeting_room_id = roomId
            updateData.meeting_room_link = generateRoomLink(roomId)
          }
        }
      }

      const { data: eventRow, error: updateError } = await (client as any)
        .from('calendar_events')
        .update(updateData)
        .eq('id', input.id)
        .eq('owner_id', adminUser.id) // Ensure user owns the event
        .select()
        .single() as { data: CalendarEventRow | null; error: any }

      if (updateError) throw updateError
      if (!eventRow) throw new Error('Failed to update event or event not found')

      // Handle attendee updates if provided
      if (input.attendees !== undefined) {
        // Remove existing non-organizer attendees
        await (client as any)
          .from('calendar_event_attendees')
          .delete()
          .eq('event_id', input.id)
          .eq('is_organizer', false)

        // Add new attendees
        for (const attendeeInput of input.attendees) {
          await (client as any)
            .from('calendar_event_attendees')
            .insert({
              event_id: input.id,
              admin_user_id: attendeeInput.adminUserId || null,
              external_email: attendeeInput.externalEmail || null,
              external_name: attendeeInput.externalName || null,
              response_status: 'pending',
              is_organizer: false,
              is_optional: attendeeInput.isOptional || false,
            })
        }

        // Send invites to new attendees
        if (input.sendInvites) {
          await sendMeetingInvites(eventRow, input.attendees, {
            id: adminUser.id,
            email: adminUser.email,
            firstName: adminUser.firstName || undefined,
            lastName: adminUser.lastName || undefined,
          }, input.sendFromAccountId)
        }
      }

      // Fetch updated event with attendees
      const { data: attendeeRows } = await (client as any)
        .from('calendar_event_attendees')
        .select('*')
        .eq('event_id', input.id) as { data: CalendarEventAttendeeRow[] | null }

      const attendees = (attendeeRows || []).map(attendeeRowToAttendee)

      return eventRowToEvent(eventRow, attendees)
    } catch (err: any) {
      console.error('Error updating calendar event:', err)
      setError(err.message)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [adminUser])

  // Delete an event
  const deleteEvent = useCallback(async (eventId: string): Promise<boolean> => {
    if (!adminUser?.id) {
      setError('You must be logged in to delete events')
      return false
    }

    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      const { error: deleteError } = await (client as any)
        .from('calendar_events')
        .delete()
        .eq('id', eventId)
        .eq('owner_id', adminUser.id) // Ensure user owns the event

      if (deleteError) throw deleteError

      return true
    } catch (err: any) {
      console.error('Error deleting calendar event:', err)
      setError(err.message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [adminUser])

  // Update attendee response
  const respondToEvent = useCallback(async (
    eventId: string,
    response: 'accepted' | 'declined' | 'tentative'
  ): Promise<boolean> => {
    if (!adminUser?.id) {
      setError('You must be logged in to respond to events')
      return false
    }

    setIsLoading(true)
    setError(null)

    try {
      const client = getNexusClient()

      const { error: updateError } = await (client as any)
        .from('calendar_event_attendees')
        .update({ response_status: response })
        .eq('event_id', eventId)
        .eq('admin_user_id', adminUser.id)

      if (updateError) throw updateError

      return true
    } catch (err: any) {
      console.error('Error responding to event:', err)
      setError(err.message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [adminUser])

  return {
    createEvent,
    updateEvent,
    deleteEvent,
    respondToEvent,
    isLoading,
    error,
  }
}

// ============================================================================
// useUpcomingEvents - Fetch upcoming events for the current user
// ============================================================================

export function useUpcomingEvents(limit = 5) {
  const { adminUser } = useAuth()
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!adminUser?.id) {
      setEvents([])
      return
    }

    async function fetchUpcoming() {
      setIsLoading(true)
      setError(null)

      try {
        const client = getNexusClient()
        const now = new Date().toISOString()

        // Get events owned by user or where user is attendee
        const { data: ownedEvents } = await (client as any)
          .from('calendar_events')
          .select('*')
          .eq('owner_id', adminUser!.id)
          .gte('start_time', now)
          .order('start_time', { ascending: true })
          .limit(limit) as { data: CalendarEventRow[] | null }

        // Get events where user is an attendee
        const { data: attendeeRecords } = await (client as any)
          .from('calendar_event_attendees')
          .select('event_id')
          .eq('admin_user_id', adminUser!.id)
          .eq('is_organizer', false) as { data: { event_id: string }[] | null }

        let attendedEvents: CalendarEventRow[] = []
        if (attendeeRecords && attendeeRecords.length > 0) {
          const eventIds = attendeeRecords.map(r => r.event_id)
          const { data } = await (client as any)
            .from('calendar_events')
            .select('*')
            .in('id', eventIds)
            .gte('start_time', now)
            .order('start_time', { ascending: true })
            .limit(limit) as { data: CalendarEventRow[] | null }

          attendedEvents = data || []
        }

        // Combine and sort
        const allEvents = [...(ownedEvents || []), ...attendedEvents]
          .filter((event, index, self) =>
            index === self.findIndex(e => e.id === event.id)
          )
          .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
          .slice(0, limit)

        setEvents(allEvents.map(row => eventRowToEvent(row)))
      } catch (err: any) {
        console.error('Error fetching upcoming events:', err)
        setError(err.message)
      } finally {
        setIsLoading(false)
      }
    }

    fetchUpcoming()
  }, [adminUser, limit])

  return { events, isLoading, error }
}

// ============================================================================
// Helper Functions
// ============================================================================

async function sendMeetingInvites(
  event: CalendarEventRow,
  attendees: CreateEventInput['attendees'],
  organizer: { id: string; email: string; firstName?: string; lastName?: string },
  sendFromAccountId?: string
) {
  if (!attendees || attendees.length === 0) return

  const client = getNexusClient()

  // Use specified account or get organizer's first active email account
  let accountId = sendFromAccountId

  if (!accountId) {
    const { data: emailAccounts } = await (client as any)
      .from('user_email_accounts')
      .select('id')
      .eq('admin_user_id', organizer.id)
      .eq('is_active', true)
      .limit(1) as { data: { id: string }[] | null }

    if (!emailAccounts || emailAccounts.length === 0) {
      console.warn('No email account configured for organizer')
      return
    }

    accountId = emailAccounts[0].id
  }
  const organizerName = organizer.firstName && organizer.lastName
    ? `${organizer.firstName} ${organizer.lastName}`
    : organizer.email

  // Format date/time with timezone info
  const startDate = new Date(event.start_time)
  const endDate = new Date(event.end_time)
  const organizerTz = event.timezone || 'America/Los_Angeles'

  // Get timezone abbreviation (e.g., PST, EST)
  const getTimezoneAbbr = (date: Date, tz: string) => {
    return date.toLocaleTimeString('en-US', { timeZone: tz, timeZoneName: 'short' }).split(' ').pop() || tz
  }

  const organizerTzAbbr = getTimezoneAbbr(startDate, organizerTz)
  const estTzAbbr = getTimezoneAbbr(startDate, 'America/New_York')

  // Format date
  const dateStr = startDate.toLocaleDateString('en-US', {
    timeZone: organizerTz,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  // Format time in organizer's timezone
  const formatTime = (date: Date, tz: string) => {
    return date.toLocaleTimeString('en-US', {
      timeZone: tz,
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const startTimeOrg = formatTime(startDate, organizerTz)
  const endTimeOrg = formatTime(endDate, organizerTz)
  const startTimeEst = formatTime(startDate, 'America/New_York')
  const endTimeEst = formatTime(endDate, 'America/New_York')

  // Build time string with both timezones
  const timeStr = `${startTimeOrg} - ${endTimeOrg} ${organizerTzAbbr}`
  const timeStrEst = organizerTz !== 'America/New_York'
    ? `(${startTimeEst} - ${endTimeEst} ${estTzAbbr})`
    : ''

  // Build email content - Mentatix branded (dark theme, yellow accent #facc15)
  const subject = `Meeting Invitation: ${event.title}`

  const meetingButton = event.meeting_room_link
    ? `<tr><td style="padding:24px 0 0;"><a href="${event.meeting_room_link}" style="display:inline-block;background:#facc15;color:#0a0a0a;font-weight:600;text-decoration:none;padding:14px 28px;border-radius:8px;font-size:14px;">Join Video Meeting</a></td></tr>`
    : ''

  const locationRow = event.location
    ? `<tr><td style="padding:12px 0;border-bottom:1px solid #262626;"><span style="color:#737373;font-size:13px;">Location</span><br><span style="color:#f5f5f5;font-size:15px;">${event.location}</span></td></tr>`
    : ''

  const descriptionSection = event.description
    ? `<tr><td style="padding:24px 0 0;"><p style="margin:0 0 8px;color:#737373;font-size:13px;">Description</p><p style="margin:0;color:#d4d4d4;font-size:14px;line-height:1.6;">${event.description}</p></td></tr>`
    : ''

  // Mentatix dark theme email template (single line to avoid =20 encoding)
  const estLine = timeStrEst ? `<br><span style="color:#737373;font-size:13px;">${timeStrEst}</span>` : ''
  const body = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head><body style="margin:0;padding:0;background:#0a0a0a;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;"><table width="100%" cellpadding="0" cellspacing="0" style="background:#0a0a0a;padding:40px 20px;"><tr><td align="center"><table width="100%" cellpadding="0" cellspacing="0" style="max-width:560px;"><tr><td style="padding:0 0 32px;text-align:center;"><span style="font-size:28px;font-weight:300;color:#e5e5e5;letter-spacing:1px;">Mentatix</span><span style="color:#facc15;font-size:28px;font-weight:300;">.</span></td></tr><tr><td style="background:#141414;border-radius:16px;border:1px solid #262626;padding:40px;"><table width="100%" cellpadding="0" cellspacing="0"><tr><td style="padding:0 0 24px;border-bottom:1px solid #262626;"><span style="display:inline-block;background:#facc15;color:#0a0a0a;font-size:11px;font-weight:600;padding:4px 10px;border-radius:4px;text-transform:uppercase;letter-spacing:0.5px;">Meeting Invitation</span></td></tr><tr><td style="padding:24px 0;"><h1 style="margin:0 0 8px;font-size:24px;font-weight:500;color:#f5f5f5;letter-spacing:-0.3px;">${event.title}</h1><p style="margin:0;color:#737373;font-size:14px;">Organized by ${organizerName}</p></td></tr><tr><td style="padding:12px 0;border-bottom:1px solid #262626;"><span style="color:#737373;font-size:13px;">When</span><br><span style="color:#f5f5f5;font-size:15px;font-weight:500;">${dateStr}</span><br><span style="color:#a3a3a3;font-size:14px;">${timeStr}</span>${estLine}</td></tr>${locationRow}${descriptionSection}${meetingButton}</table></td></tr><tr><td style="padding:32px 0 0;text-align:center;"><p style="margin:0;color:#a3a3a3;font-size:12px;">Sent via <a href="https://mentatix.io" style="color:#facc15;text-decoration:none;">Mentatix</a></p></td></tr></table></td></tr></table></body></html>`

  // Send to each attendee
  for (const attendee of attendees) {
    const email = attendee.externalEmail || attendee.adminUserId
    if (!email) continue

    // If internal user, get their email
    let recipientEmail = attendee.externalEmail
    if (attendee.adminUserId) {
      const { data: user } = await (client as any)
        .from('admin_users')
        .select('email')
        .eq('id', attendee.adminUserId)
        .single() as { data: { email: string } | null }
      recipientEmail = user?.email
    }

    if (!recipientEmail) continue

    try {
      await mailApi.sendEmail({
        accountId,
        to: [recipientEmail],
        subject,
        body,
        isHtml: true,
      })

      // Mark invite as sent
      await (client as any)
        .from('calendar_event_attendees')
        .update({ invite_sent_at: new Date().toISOString() })
        .eq('event_id', event.id)
        .or(
          attendee.adminUserId
            ? `admin_user_id.eq.${attendee.adminUserId}`
            : `external_email.eq.${attendee.externalEmail}`
        )
    } catch (err) {
      console.error('Failed to send invite to', recipientEmail, err)
    }
  }
}

// Export types for convenience
export type { CalendarEvent, EventAttendee, CreateEventInput, UpdateEventInput }
